const assert = require('#utils/browser-assert')

const { hypot, max, min, sqrt } = Math

/*
  Find the control points on either side of p1 that will allow us to smoothly
  join p0->p1->p2 using two bezier curves.

  Note that this function returns control points on either side of a knot. To
  draw the curve between two knots we need two control points that fall between
  the knots being joined. Therefore a single call to this function is not enough
  to draw the bezier curve we're after.

  For the full geometric proof, see:
  http://scaledinnovation.com/analytics/splines/aboutSplines.html
*/
function findControlPointsForSmoothConnection(p0, p1, p2, tightness) {
  // Distances from p1 to p0 and p2
  const d01 = hypot(p1.x - p0.x, p1.y - p0.y)
  const d12 = hypot(p2.x - p1.x, p2.y - p1.y)
  // Scaling factors
  const fa = tightness * d01 / (d01 + d12)
  const fb = tightness - fa
  // control point between p0 and p1
  const cp1 = {
    x: p1.x + fa * (p0.x - p2.x),
    y: p1.y + fa * (p0.y - p2.y),
  }
  // control point between p1 and p2
  const cp2 = {
    x: p1.x - fb * (p0.x - p2.x),
    y: p1.y - fb * (p0.y - p2.y),
  }
  return [cp1, cp2]
}

function findControlPointsForSmoothConnections(points, tightness) {
  return points.map((curr, i) => {
    // Wrap around as though the curve will be closed. If it isn't closed we
    // don't need the control points on either side of the first or last knot,
    // but... meh. Calculate them anyways.
    const frst = points[0]
    const last = points[points.length - 1]
    const prev = (curr === frst) ? last : points[i - 1]
    const next = (curr === last) ? frst : points[i + 1]
    return findControlPointsForSmoothConnection(prev, curr, next, tightness)
  })
}

/*
  p1 is the start
  p2 is the first control point
  p3 is the second control point
  p4 is the end
*/
function getCubicBezierAABB(p0, p1, p2, p3) {
  // The cubic bezier function is only defined for t=[0..1]
  const inRange = (x) => 0 <= x && x <= 1
  // The derivative of a cubic curve is a quadratic curve, so we can use the
  // quadratic formula to find the roots (i.e. the extremities)
  const _xRoots = quadraticFormula(p0.x, p1.x, p2.x, p3.x)
  const _yRoots = quadraticFormula(p0.y, p1.y, p2.y, p3.y)
  const xRoots = _xRoots.filter(inRange)
  const yRoots = _yRoots.filter(inRange)
  // Plug the roots (plus 0 and 1) into the cubic bezier function
  const ftx = [0, 1, ...xRoots].map(t => cubicBezier(p0.x, p1.x, p2.x, p3.x, t))
  const fty = [0, 1, ...yRoots].map(t => cubicBezier(p0.y, p1.y, p2.y, p3.y, t))

  assert(ftx.length > 0)
  assert(fty.length > 0)
  // Keep the largest and smallest values we found
  const xmin =  min(...ftx)
  const xmax =  max(...ftx)
  const ymin =  min(...fty)
  const ymax =  max(...fty)

  return {
    x: xmin,
    y: ymin,
    width: xmax - xmin,
    height: ymax - ymin,
  }
}

/*
  p1 is the start
  p2 is the control point
  p3 is the end
*/
function getQuadraticBezierAABB(p1, p2, p3) {
  const { max, min } = Math
  const findRoot = (a, b, c) => clamp(0, 1, ((a - b) / (a - 2*b + c)))

  let xmin = min(p1.x, p3.x)
  let ymin = min(p1.y, p3.y)
  let xmax = max(p1.x, p3.x)
  let ymax = max(p1.y, p3.y)
  if (p2.x < xmin || xmax < p2.x || p2.y < ymin || ymax < p2.y) {
    const tx = findRoot(p1.x, p2.x, p3.x)
    const ty = findRoot(p1.y, p2.y, p3.y)
    const qx = quadraticBezier(p1.x, p2.x, p3.x, tx)
    const qy = quadraticBezier(p1.y, p2.y, p3.y, ty)
    xmin = min(xmin, qx)
    ymin = min(ymin, qy)
    xmax = max(xmax, qx)
    ymax = max(ymax, qy)
  }
  return {
    x: xmin,
    y: ymin,
    width: xmax - xmin,
    height: ymax - ymin,
  }
}

function clamp(lower, upper, x) {
  const { max, min } = Math
  return max(lower, min(upper, x))
}

function quadraticBezier(p1, p2, p3, t) {
  return p1*(1-t)**2 + 2*p2*t*(1-t) + p3*t**2
}

function cubicBezier(p1, p2, p3, p4, t) {
  return p1*(1-t)**3 + 3*p2*t*(1-t)**2 + 3*p3*t**2*(1-t) + p4*t**3
}

function determinant(a, b, c) {
  return b*b - 4*a*c
}

function quadraticFormula(p1, p2, p3, p4) {
  const a = 3 * (-p1 + 3*p2 - 3*p3 + p4)
  const b = 6 * (p1 - 2*p2 + p3)
  const c = 3 * (p2 - p1)
  const d = determinant(a, b, c)
  if (d < 0) {
    return []

  } else if (a === 0) {
    return [-c / b]

  } else if (d === 0) {
    return [
      (-b + sqrt(d)) / (2*a)
    ]

  } else {
    return [
      (-b + sqrt(d)) / (2*a),
      (-b - sqrt(d)) / (2*a),
    ]
  }
}

module.exports = {
  cubicBezier,
  findControlPointsForSmoothConnections,
  getCubicBezierAABB,
  getQuadraticBezierAABB,
  quadraticBezier,
}
